/**
 * SanPlumb — Constructor de escena 3D y motor de routing automático
 *
 * Dos responsabilidades:
 *
 *  1. `buildSceneFromData()` — orquestador puro: toma un SystemData y un
 *     `THREE.Scene`, limpia la escena, ensambla piso/muros/planos NPT/tuberías/
 *     accesorios/sanitarios. Origen: legacy MVP líneas 1637-1692.
 *
 *  2. `applyStrategy()` + `getNextStrategy()` — motor de ROUTING AUTOMÁTICO
 *     (nuevo en Fase 2). A partir de los sanitarios y la habitación, genera
 *     las tuberías y accesorios que materializan una de las tres estrategias:
 *       A) muro_dual — AF + AC en el mismo muro, separadas horizontalmente
 *       B) mixta     — AF en muro + AC por contrapiso
 *       C) piso_dual — ambas por contrapiso
 *     `getNextStrategy()` rota A → B → C → A teniendo en cuenta qué
 *     estrategias son viables para el `murW` dado.
 *
 * El routing automático SOLO se usa cuando `data.routingSource === 'auto_generated'`.
 * Si `routingSource === 'user_defined'`, las pipes existentes se respetan y
 * `applyStrategy` no se invoca.
 */

import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import type {
  SystemData,
  RoutingStrategy,
  Sanitary,
  Room,
  AFPipe,
  ACPipe,
  AFAccessory,
  ACAccessory,
  Vec3,
} from '../../types';
import { allPipes, allAccessories } from '../../types';
import { COLORS, accessoryColor } from './colors';
import {
  mkPipe,
  mkAcc,
  mkCodo,
  mkCodoDirected,
  mkTee,
  mkTeeDirected,
  mkReduction,
  mkReductionDirected,
  mkLlavePaso,
  mkSanitary,
  mkRegadera,
} from './geometry';
import { mkFloor, mkWalls, mkHPlane } from './environment';
import { ESTRATEGIA_DISPONIBLE, NPT_HEIGHTS, SEPARACION_TUBERIAS } from '../../constants/paeba';

const EPS = 0.0001;

function samePoint(a: Vec3, b: Vec3): boolean {
  return (
    Math.abs(a[0] - b[0]) < EPS &&
    Math.abs(a[1] - b[1]) < EPS &&
    Math.abs(a[2] - b[2]) < EPS
  );
}

function directionFromJoint(joint: Vec3, other: Vec3): Vec3 {
  return [other[0] - joint[0], other[1] - joint[1], other[2] - joint[2]] as Vec3;
}

function pointOnSegment(p: Vec3, a: Vec3, b: Vec3): boolean {
  const ab = new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
  const ap = new THREE.Vector3(p[0] - a[0], p[1] - a[1], p[2] - a[2]);
  const lenSq = ab.lengthSq();
  if (lenSq < EPS) return false;
  const t = ap.dot(ab) / lenSq;
  if (t <= EPS || t >= 1 - EPS) return false;
  const closest = new THREE.Vector3(a[0], a[1], a[2]).add(ab.multiplyScalar(t));
  return closest.distanceTo(new THREE.Vector3(p[0], p[1], p[2])) < EPS;
}

function connectedDirections(data: SystemData, system: 'AF' | 'AC', pos: Vec3): Vec3[] {
  const pipes = system === 'AF' ? data.pipes : data.pipes_ac;
  const dirs: Vec3[] = [];
  for (const p of pipes) {
    if (samePoint(p.start, pos)) dirs.push(directionFromJoint(pos, p.end));
    else if (samePoint(p.end, pos)) dirs.push(directionFromJoint(pos, p.start));
    else if (pointOnSegment(pos, p.start, p.end)) {
      dirs.push(directionFromJoint(pos, p.start));
      dirs.push(directionFromJoint(pos, p.end));
    }
  }
  return dirs;
}

function outletDirectionForAccessory(type: string): Vec3 | null {
  if (type === 'Codo galvanizado' || type === 'Adaptador + niple galvanizado') {
    return [0, 0, 1] as Vec3;
  }
  return null;
}

function uniqueDirections(dirs: Vec3[]): Vec3[] {
  const out: Vec3[] = [];
  for (const d of dirs) {
    const v = new THREE.Vector3(...d);
    if (v.length() < EPS) continue;
    v.normalize();
    const duplicate = out.some((o) => {
      const ov = new THREE.Vector3(...o).normalize();
      return ov.dot(v) > 0.98;
    });
    if (!duplicate) out.push([v.x, v.y, v.z] as Vec3);
  }
  return out;
}

type RenderableAccessory = AFAccessory | ACAccessory;

function roundedPointKey(pos: Vec3): string {
  return pos.map((v) => Math.round(v * 1000)).join('|');
}

function buildAccessoryRenderPositions(accessories: readonly RenderableAccessory[]): Map<string, Vec3> {
  const grouped = new Map<string, RenderableAccessory[]>();
  for (const a of accessories) {
    const key = roundedPointKey(a.pos);
    const list = grouped.get(key);
    if (list) list.push(a);
    else grouped.set(key, [a]);
  }

  const positions = new Map<string, Vec3>();
  for (const list of grouped.values()) {
    if (list.length <= 1) continue;
    const spread = 0.035;
    const center = (list.length - 1) / 2;
    list.forEach((a, i) => {
      if (a.isSwitchboard) return;
      const angle = -Math.PI / 2 + (i - center) * 0.8;
      positions.set(a.id, [
        a.pos[0] + Math.cos(angle) * spread,
        a.pos[1] + (i - center) * 0.01,
        a.pos[2] + Math.sin(angle) * spread,
      ] as Vec3);
    });
  }
  return positions;
}

// ──────────────────────────────────────────────────────────────────
// Layer toggles (controlable por UI)
// ──────────────────────────────────────────────────────────────────

export interface LayerToggles {
  walls?: boolean;
  nptPlanes?: boolean;
  sanitarios?: boolean;
  medidas?: boolean;
  /** Mostrar sistema AF (tuberías + accesorios). Default true. */
  showAF?: boolean;
  /** Mostrar sistema AC (tuberías + accesorios). Default true. */
  showAC?: boolean;
}

const DEFAULT_LAYERS: Required<LayerToggles> = {
  walls: true,
  nptPlanes: true,
  sanitarios: true,
  medidas: false,
  showAF: true,
  showAC: true,
};

// ──────────────────────────────────────────────────────────────────
// Tipos auxiliares para clickability
// ──────────────────────────────────────────────────────────────────

export interface ClickableUserData {
  kind: 'pipe' | 'acc' | 'sanitary';
  data: unknown;
}

export interface SceneBuildResult {
  /** Lista de meshes clickables para el raycaster del visor. */
  clickables: THREE.Object3D[];
  /** Punto al que apuntar la cámara (centro de la escena). */
  cameraTarget: THREE.Vector3;
  /** Radio sugerido para la órbita de la cámara. */
  orbitRadius: number;
  /**
   * Meshes para la animación de entrada (diseño aprobado):
   *  - `pipes`: crecen escalando su eje Y (longitud) de forma escalonada.
   *  - `fittings`: aparecen escalando de 0 tras las tuberías.
   * El driver de animación vive en useThreeScene.
   */
  animatables: { pipes: THREE.Object3D[]; fittings: THREE.Object3D[] };
}

// ──────────────────────────────────────────────────────────────────
// addLights — luces ambientales y direccionales
// ──────────────────────────────────────────────────────────────────

function addLights(scene: THREE.Scene): void {
  scene.add(new THREE.AmbientLight(0x1a2a40, 3.5));
  const l1 = new THREE.DirectionalLight(0x3060cc, 1.5);
  l1.position.set(5, 8, 3);
  scene.add(l1);
  const l2 = new THREE.DirectionalLight(0x00cc66, 0.7);
  l2.position.set(-3, 4, -4);
  scene.add(l2);
}

// ──────────────────────────────────────────────────────────────────
// buildSceneFromData — el orquestador
// ──────────────────────────────────────────────────────────────────

/**
 * Limpia el contenido de la escena dada y la repuebla con el SystemData.
 * Esta función es el punto de entrada principal del visor 3D.
 *
 * @param data    Sistema completo a renderizar.
 * @param scene   Three.js Scene preexistente (creada en useEffect del visor).
 * @param layers  Toggles de capas opcional (default: todos activos excepto medidas).
 */
export function buildSceneFromData(
  data: SystemData,
  scene: THREE.Scene,
  layers: LayerToggles = {},
): SceneBuildResult {
  const L = { ...DEFAULT_LAYERS, ...layers };

  // Limpiar escena
  while (scene.children.length) {
    scene.remove(scene.children[0]);
  }

  const clickables: THREE.Object3D[] = [];
  const animPipes: THREE.Object3D[] = [];
  const animFittings: THREE.Object3D[] = [];
  addLights(scene);

  // Piso + muros
  scene.add(mkFloor(data.room.w, data.room.d));
  if (L.walls) {
    scene.add(mkWalls(data.room));
  }

  // Planos NPT de referencia: NPT ±0.00 (piso terminado) + NPT losa de
  // piso (debajo del contrapiso). Decisión v1.13 — el plano superior NO
  // es el techo, sino la losa estructural sobre la que descansa el
  // contrapiso. Útil para perforaciones de desagüe que pasan a través de
  // la losa.
  if (L.nptPlanes) {
    scene.add(mkHPlane(0, COLORS.AF, data.room.w, data.room.d));
    scene.add(mkHPlane(-data.room.contrapiso, COLORS.acc, data.room.w, data.room.d));
  }

  // Tuberías (AF + AC) — filtradas por toggles showAF / showAC
  for (const p of allPipes(data)) {
    if (p.system === 'AF' && !L.showAF) continue;
    if (p.system === 'AC' && !L.showAC) continue;
    const m = mkPipe(p.start, p.end, p.system, p.diam);
    if (!m) continue;
    m.userData = { kind: 'pipe', data: p } satisfies ClickableUserData;
    scene.add(m);
    clickables.push(m);
    animPipes.push(m);

    // Toggle MEDIDAS: etiqueta CSS2D estilo cota del diseño aprobado
    // (∅ ½" AF · +0.30 NPT). Solo en mains y risers para no saturar la escena.
    // CSS2DRenderer vive en useThreeScene; .pipe-label se estiliza en App.css.
    if (L.medidas && (p.id.startsWith('main_') || p.id.endsWith('_riser'))) {
      const div = document.createElement('div');
      div.className = `pipe-label pipe-label-${p.system.toLowerCase()}`;
      const y = Math.max(p.start[1], p.end[1]);
      div.textContent = `∅ ${p.diam} ${p.system} · +${y.toFixed(2)} NPT`;
      const label = new CSS2DObject(div);
      label.position.set(
        (p.start[0] + p.end[0]) / 2,
        (p.start[1] + p.end[1]) / 2 + 0.05,
        (p.start[2] + p.end[2]) / 2,
      );
      scene.add(label);
    }
  }

  // Accesorios (AF + AC) — filtrados por toggles showAF / showAC
  const accessories = allAccessories(data);
  const accessoryRenderPositions = buildAccessoryRenderPositions(accessories);
  for (const a of accessories) {
    if (a.system === 'AF' && !L.showAF) continue;
    if (a.system === 'AC' && !L.showAC) continue;
    const renderPos = accessoryRenderPositions.get(a.id) ?? a.pos;
    if (a.isSwitchboard) {
      // Llave de paso — ensamblaje en U §VII
      const lpGroup = mkLlavePaso(renderPos, a.system);
      lpGroup.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      // Propagar userData a los hijos para que el raycaster los detecte
      lpGroup.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          c.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
          clickables.push(c);
        }
      });
      scene.add(lpGroup);
      animFittings.push(lpGroup);
    } else if (a.color === 'reduction' || a.color === 'reduction_ac') {
      const dirs = connectedDirections(data, a.system, a.pos);
      const m =
        dirs.length > 0
          ? mkReductionDirected(renderPos, accessoryColor(a.color), dirs, true)
          : mkReduction(renderPos, true);
      m.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      m.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          c.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
          clickables.push(c);
        }
      });
      scene.add(m);
      clickables.push(m);
      animFittings.push(m);
    } else if (a.type === 'Regadera') {
      // Cabezal de regadera apuntando hacia abajo (§VI ducha_brazo ≈ 2.05 m)
      const g = mkRegadera(renderPos, accessoryColor(a.color));
      g.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      g.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          c.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
          clickables.push(c);
        }
      });
      scene.add(g);
      animFittings.push(g);
    } else if (
      a.type === 'Codo 90°' ||
      a.type === 'Codo 90 deg' ||
      a.type === 'Codo galvanizado' ||
      a.type === 'Adaptador + niple galvanizado'
    ) {
      // Codo curvado (¼ torus) en lugar de la esferita genérica. La
      // orientación neutra es suficiente visualmente para el visor —
      // los codos quedan más realistas en cualquier ángulo de cámara.
      const diam = a.spec.includes('3/4') ? ('3/4"' as const) : ('1/2"' as const);
      const wallOutlet = outletDirectionForAccessory(a.type);
      const dirs = uniqueDirections([
        ...connectedDirections(data, a.system, a.pos),
        ...(wallOutlet ? [wallOutlet] : []),
      ]);
      const m =
        dirs.length >= 2
          ? mkCodoDirected(renderPos, accessoryColor(a.color), diam, [dirs[0], dirs[1]])
          : mkCodo(renderPos, accessoryColor(a.color), diam);
      m.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      m.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          c.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
          clickables.push(c);
        }
      });
      scene.add(m);
      clickables.push(m);
      animFittings.push(m);
    } else if (a.type === 'Tee' || a.type === 'Tee mezcladora' || a.type === 'Tee reductora') {
      // Tee de tres vías (cuerpo pasante + derivación perpendicular)
      const diam = a.spec.includes('3/4') ? ('3/4"' as const) : ('1/2"' as const);
      const dirs = uniqueDirections(connectedDirections(data, a.system, a.pos));
      const g =
        dirs.length >= 3
          ? mkTeeDirected(renderPos, accessoryColor(a.color), diam, dirs)
          : mkTee(renderPos, accessoryColor(a.color), diam);
      g.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      g.traverse((c) => {
        if ((c as THREE.Mesh).isMesh) {
          c.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
          clickables.push(c);
        }
      });
      scene.add(g);
      animFittings.push(g);
    } else {
      const m = mkAcc(renderPos, accessoryColor(a.color));
      m.userData = { kind: 'acc', data: a } satisfies ClickableUserData;
      scene.add(m);
      clickables.push(m);
      animFittings.push(m);
    }
  }

  // Sanitarios
  if (L.sanitarios) {
    for (const s of data.sanitaries) {
      const g = mkSanitary(s);
      g.userData = { kind: 'sanitary', data: s } satisfies ClickableUserData;
      g.children.forEach((c) => {
        c.userData = { kind: 'sanitary', data: s } satisfies ClickableUserData;
        clickables.push(c);
      });
      scene.add(g);
    }
  }

  // Cámara: target en centro del cuarto, altura ~28% para encuadre isométrico
  const cameraTarget = new THREE.Vector3(data.room.w / 2, data.room.h * 0.28, data.room.d / 2);
  const orbitRadius = Math.max(data.room.w, data.room.d) * 2.5;

  return {
    clickables,
    cameraTarget,
    orbitRadius,
    animatables: { pipes: animPipes, fittings: animFittings },
  };
}

// ──────────────────────────────────────────────────────────────────
// getAvailableStrategies — qué estrategias son viables para un murW
// ──────────────────────────────────────────────────────────────────

/**
 * Devuelve la lista de estrategias viables para un espesor de muro dado.
 * Una estrategia es "viable" si el muro tiene espesor suficiente para
 * alojar las tuberías que esa estrategia requiere en muro (si las hay).
 *
 * En la práctica, casi todos los muros peruanos (≥ 6 cm) soportan las
 * 3 estrategias. La función existe para casos extremos (muros muy delgados).
 */
export function getAvailableStrategies(murW: number): RoutingStrategy[] {
  const out: RoutingStrategy[] = [];
  if (murW >= ESTRATEGIA_DISPONIBLE.MIN_MURW_MURO_DUAL) out.push('muro_dual');
  if (murW >= ESTRATEGIA_DISPONIBLE.MIN_MURW_MIXTA) out.push('mixta');
  if (murW >= ESTRATEGIA_DISPONIBLE.MIN_MURW_PISO_DUAL) out.push('piso_dual');
  // piso_dual siempre está disponible (min 0) — defensivo si la tabla cambia
  if (out.length === 0) out.push('piso_dual');
  return out;
}

/** Orden cíclico de rotación entre estrategias (Regenerar 3D). */
const STRATEGY_CYCLE: readonly RoutingStrategy[] = ['muro_dual', 'mixta', 'piso_dual'];

/**
 * Devuelve la siguiente estrategia válida en el ciclo A → B → C → A.
 * Salta las estrategias no disponibles para el murW dado.
 *
 * @param current  Estrategia activa.
 * @param murW     Espesor del muro (para filtrar disponibilidad).
 */
export function getNextStrategy(current: RoutingStrategy, murW: number): RoutingStrategy {
  const available = getAvailableStrategies(murW);
  const currentIdx = STRATEGY_CYCLE.indexOf(current);

  // Buscar la siguiente estrategia EN EL CICLO que esté disponible
  for (let offset = 1; offset <= STRATEGY_CYCLE.length; offset++) {
    const next = STRATEGY_CYCLE[(currentIdx + offset) % STRATEGY_CYCLE.length];
    if (available.includes(next)) return next;
  }

  // Defensivo: si nada está disponible (no debería pasar), devolver current
  return current;
}

// ──────────────────────────────────────────────────────────────────
// applyStrategy — generador de routing automático
// ──────────────────────────────────────────────────────────────────

/**
 * Convención §VI/§VII del diseño aprobado:
 *  - AF sale +10 cm a la DERECHA del eje del aparato (excepción inodoro: −15 cm
 *    a la izquierda, porque la entrada del tanque está abajo-izquierda).
 *  - AC sale −10 cm a la IZQUIERDA del eje.
 *  - Las montantes (risers) suben detrás del MURO trasero (z pequeño), con AC
 *    en la capa pegada al muro y AF más atrás. Nunca comparten el mismo (x,z).
 */
const WALL_BACK_OUTSIDE_Z = -0.04;
const Z_RISER_AC = WALL_BACK_OUTSIDE_Z; // AC va pegado a la cara posterior del muro
const Z_RISER_AF = WALL_BACK_OUTSIDE_Z - SEPARACION_TUBERIAS.RECOMENDADA; // AF queda mas atras

/** Configuración espacial de cada estrategia (dónde corren los mains). */
interface StrategyConfig {
  y_main_af: number; // altura del main AF
  y_main_ac: number; // altura del main AC
  z_main_af: number; // z del main AF (en muro o piso)
  z_main_ac: number; // z del main AC
  /** AF necesita bridge horizontal main→muro (cuando el main no está en el muro). */
  needs_bridge_af: boolean;
  /** AC necesita bridge horizontal main→muro. */
  needs_bridge_ac: boolean;
}

function getStrategyConfig(strategy: RoutingStrategy): StrategyConfig {
  switch (strategy) {
    case 'muro_dual':
      // AF y AC ambos en el muro (y=0.30), separados en z. Risers directos.
      return {
        y_main_af: NPT_HEIGHTS.alimentacion_af, // 0.30
        y_main_ac: NPT_HEIGHTS.alimentacion_af, // 0.30
        z_main_af: Z_RISER_AF,
        z_main_ac: Z_RISER_AC,
        needs_bridge_af: false,
        needs_bridge_ac: false,
      };
    case 'mixta':
      // AF en muro (0.30), AC por contrapiso (0.05) y sube al muro.
      return {
        y_main_af: NPT_HEIGHTS.alimentacion_af, // 0.30 (muro)
        y_main_ac: NPT_HEIGHTS.alimentacion_ac, // 0.05 (piso)
        z_main_af: Z_RISER_AF,
        z_main_ac: 0.55, // AC corre por el centro del piso
        needs_bridge_af: false,
        needs_bridge_ac: true,
      };
    case 'piso_dual':
      // AF y AC ambos por contrapiso, suben al muro en cada aparato.
      return {
        y_main_af: NPT_HEIGHTS.alimentacion_ac, // 0.05 (piso)
        y_main_ac: NPT_HEIGHTS.alimentacion_ac, // 0.05 (piso)
        z_main_af: 0.5,
        z_main_ac: 0.5 + SEPARACION_TUBERIAS.RECOMENDADA,
        needs_bridge_af: true,
        needs_bridge_ac: true,
      };
  }
}

/** Conjunto de pipes/accesorios generados por la estrategia. */
export interface StrategyResult {
  pipes: AFPipe[];
  pipes_ac: ACPipe[];
  accessories: AFAccessory[];
  accessories_ac: ACAccessory[];
}

/** Offset en X del punto de salida AF respecto al eje del aparato (§VI). */
function afXOffset(s: Sanitary): number {
  // Excepción inodoro: AF a la IZQUIERDA −15 cm (entrada del tanque).
  return s.type === 'Inodoro' ? -0.15 : 0.1;
}

function isLateralLavadero(s: Sanitary): boolean {
  return s.type.startsWith('Lavadero') && s.subtype.toLowerCase().includes('lateral');
}

function fixtureRiserZ(s: Sanitary, defaultZ: number): number {
  if (!isLateralLavadero(s)) return defaultZ;
  return Math.max(defaultZ, s.pos[2] - s.d / 2 - 0.04);
}

/** Offset en X del punto de salida AC (siempre IZQUIERDA −10 cm). */
const AC_X_OFFSET = -0.1;

/**
 * Genera tuberías y accesorios automáticamente para una estrategia dada,
 * a partir de los sanitarios y la habitación.
 *
 * Modelo espacial (diseño aprobado, §VI/§VII):
 *  1. Llaves de paso AF/AC (ensamblaje en U) al inicio del baño.
 *  2. Main AF y main AC horizontales (en muro o piso según estrategia).
 *  3. Por cada aparato:
 *     - AF: tee en el main → [bridge al muro si el main no está en el muro]
 *       → montante en x=eje+offset, z=muro → codo de salida.
 *     - AC (si has_ac): idem en x=eje−10 cm, z separado del AF.
 *  Como AF y AC suben en x DISTINTOS (derecha vs izquierda) y z distintos,
 *  jamás se superponen.
 */
export function applyStrategy(
  strategy: RoutingStrategy,
  sanitaries: readonly Sanitary[],
  room: Room,
): StrategyResult {
  const cfg = getStrategyConfig(strategy);
  const result: StrategyResult = {
    pipes: [],
    pipes_ac: [],
    accessories: [],
    accessories_ac: [],
  };

  if (sanitaries.length === 0) return result;

  // El main debe cubrir TODAS las columnas de salida (eje ± offset).
  const riserXs: number[] = [];
  for (const s of sanitaries) {
    riserXs.push(s.pos[0] + afXOffset(s));
    if (s.has_ac) riserXs.push(s.pos[0] + AC_X_OFFSET);
  }
  const minRiserX = Math.min(...riserXs);
  const maxRiserX = Math.max(...riserXs);

  // La llave de paso (ensamblaje en U §VII) tiene un ancho total ~0.42 m
  // (mkLlavePaso). El main debe SALIR del lado derecho del ensamblaje para
  // que el flujo continúe sin retroceder. LP_HALF_WIDTH se sincroniza con
  // la geometría real para que el codo derecho del ensamblaje empalme con
  // el inicio del main.
  const LP_HALF_WIDTH = 0.21;
  // LP AF y LP AC en el MURO FRONTAL del baño (z = room.d − 0.05), no en
  // el trasero (v1.19). Conectan al main del muro trasero mediante tubería
  // que baja al piso y cruza por contrapiso. Esto refleja la práctica
  // peruana real: los LP se ubican en el muro de enfrente al de los
  // sanitarios para no chocar con los aparatos.
  const Z_LP_FRONT = room.d + 0.04;
  const LP_OFFSET_AC = 0.52;
  const y_alimentacion = cfg.y_main_af; // 0.30 m altura tradicional
  const y_piso = -Math.max(0.025, room.contrapiso * 0.5);

  const x_lp_af = Math.max(LP_HALF_WIDTH + 0.02, Math.min(0.35, minRiserX - 0.05));
  const x_lp_ac = Math.max(x_lp_af + LP_OFFSET_AC, LP_HALF_WIDTH + 0.02);
  const x_main_start_af = Math.min(x_lp_af, minRiserX - 0.05);
  const x_max = Math.min(Math.max(maxRiserX, x_main_start_af + 0.3), room.w);

  // ── LP AF — cadena de entrada/salida coherente con mkLlavePaso §VII ──
  // Las verticales del ensamblaje en U están en sus EXTREMOS LATERALES:
  //   - lado izquierdo (x_lp − LP_HALF_WIDTH): entrada
  //   - lado derecho (x_lp + LP_HALF_WIDTH): salida
  // La ¾" entra por la IZQUIERDA desde el piso y la ½" sale por la
  // DERECHA hacia el piso. La alimentación ¾" baja primero desde el techo
  // (extremo izquierdo del muro frontal), cruza por contrapiso y sube al
  // LP. Antes la subida estaba en el CENTRO del LP — quedaba flotante.
  const x_lp_af_left = x_lp_af - LP_HALF_WIDTH;
  const x_lp_af_right = x_lp_af + LP_HALF_WIDTH;
  // El ramal ¾" previo a la reducción se representa solo como una subida
  // vertical bajo la reducción, sin codo inferior para no asumir dirección.
  result.pipes.push({
    id: 'subida_lp_af',
    label: 'AF — Ramal vertical ¾" hasta reducción',
    start: [x_lp_af_left, y_piso, Z_LP_FRONT] as Vec3,
    end: [x_lp_af_left, y_alimentacion, Z_LP_FRONT] as Vec3,
    diam: '3/4"',
    len: y_alimentacion - y_piso,
    system: 'AF',
    mat: 'PVC-P',
  });
  // Reducción ¾→½ JUSTO en el lado izquierdo del LP (entrada al ensamblaje)
  result.accessories.push({
    id: 'red_entrada_af',
    type: 'Reducción',
    spec: 'Reducción PVC-P 3/4"→1/2" (entrada al LP)',
    pos: [x_lp_af_left, y_alimentacion, Z_LP_FRONT],
    color: 'reduction',
    system: 'AF',
  });
  // Llave de paso AF (ensamblaje en U) — basePos centrado en x_lp_af
  result.accessories.push({
    id: 'lp_af_auto',
    type: 'Llave de paso',
    spec: `Llave de paso AF ½" — ensamblaje U completo (estrategia ${strategy})`,
    pos: [x_lp_af, y_alimentacion, Z_LP_FRONT],
    color: 'llave_af',
    system: 'AF',
    isSwitchboard: true,
  });
  // Bajada ½" desde el lado DERECHO del LP al piso
  result.pipes.push({
    id: 'lp_af_down',
    label: 'AF — Bajada ½" del LP al piso',
    start: [x_lp_af_right, y_alimentacion, Z_LP_FRONT] as Vec3,
    end: [x_lp_af_right, y_piso, Z_LP_FRONT] as Vec3,
    diam: '1/2"',
    len: y_alimentacion - y_piso,
    system: 'AF',
    mat: 'PVC-P',
  });
  // Alias para mantener referencias del resto del código
  const x_lp_af_out = x_lp_af_right;
  // Codo en piso (muro frontal) — recibe la bajada del LP
  result.accessories.push({
    id: 'codo_piso_af_front',
    type: 'Codo 90°',
    spec: 'Codo PVC ½" 90° (LP frontal → cruce piso AF)',
    pos: [x_lp_af_out, y_piso, Z_LP_FRONT],
    color: 'acc',
    system: 'AF',
  });
  // Cruce horizontal por contrapiso ½" — del muro frontal al trasero
  result.pipes.push({
    id: 'cruce_piso_af',
    label: 'AF — Cruce por contrapiso ½" (frontal→trasero)',
    start: [x_lp_af_out, y_piso, Z_LP_FRONT] as Vec3,
    end: [x_lp_af_out, y_piso, Z_RISER_AF] as Vec3,
    diam: '1/2"',
    len: Z_LP_FRONT - Z_RISER_AF,
    system: 'AF',
    mat: 'PVC-P',
  });
  // Codo en piso (muro trasero)
  result.accessories.push({
    id: 'codo_piso_af_back',
    type: 'Codo 90°',
    spec: 'Codo PVC ½" 90° (cruce piso → subida muro trasero AF)',
    pos: [x_lp_af_out, y_piso, Z_RISER_AF],
    color: 'acc',
    system: 'AF',
  });
  // Subida vertical ½" del piso al main del muro trasero
  result.pipes.push({
    id: 'subida_main_af',
    label: 'AF — Subida ½" al main',
    start: [x_lp_af_out, y_piso, Z_RISER_AF] as Vec3,
    end: [x_lp_af_out, y_alimentacion, Z_RISER_AF] as Vec3,
    diam: '1/2"',
    len: y_alimentacion - y_piso,
    system: 'AF',
    mat: 'PVC-P',
  });
  // Codo arriba (transición a main horizontal)
  result.accessories.push({
    id: 'codo_main_af_start',
    type: 'Codo 90°',
    spec: 'Codo PVC ½" 90° (subida → main AF)',
    pos: [x_lp_af_out, y_alimentacion, Z_RISER_AF],
    color: 'acc',
    system: 'AF',
  });
  // Main horizontal ½" en el muro trasero
  // Main horizontal del muro trasero — arranca donde sube el cruce piso
  const x_main_af_start = Math.min(x_lp_af_out, minRiserX - 0.05);
  result.pipes.push({
    id: 'main_af',
    label: `AF — Distribución principal ½" (${strategy})`,
    start: [x_main_af_start, y_alimentacion, Z_RISER_AF] as Vec3,
    end: [x_max, y_alimentacion, Z_RISER_AF] as Vec3,
    diam: '1/2"',
    len: x_max - x_main_af_start,
    system: 'AF',
    mat: 'PVC-P',
  });

  // ── LP AC (solo si hay aparatos con AC) ──
  const hasAnyAcSanitary = sanitaries.some((s) => s.has_ac);
  if (hasAnyAcSanitary) {
    // Misma cadena que AF: bajada vertical ¾" desde el techo en x_drop_ac
    // (a la derecha del drop AF para no chocar) → cruce piso ¾" → subida
    // al lado IZQUIERDO del LP → reducción → LP → bajada ½" por el lado
    // DERECHO del LP → cruce piso al muro trasero → subida al main.
    const x_lp_ac_left = x_lp_ac - LP_HALF_WIDTH;
    const x_lp_ac_right = x_lp_ac + LP_HALF_WIDTH;
    const x_lp_ac_out = x_lp_ac_right;
    result.pipes_ac.push({
      id: 'subida_lp_ac',
      label: 'AC — Ramal vertical ¾" hasta reducción',
      start: [x_lp_ac_left, y_piso, Z_LP_FRONT] as Vec3,
      end: [x_lp_ac_left, y_alimentacion, Z_LP_FRONT] as Vec3,
      diam: '3/4"',
      len: y_alimentacion - y_piso,
      system: 'AC',
      mat: 'CPVC',
    });
    result.accessories_ac.push({
      id: 'red_entrada_ac',
      type: 'Reducción',
      spec: 'Reducción CPVC 3/4"→1/2" (entrada al LP)',
      pos: [x_lp_ac_left, y_alimentacion, Z_LP_FRONT],
      color: 'reduction_ac',
      system: 'AC',
    });
    result.accessories_ac.push({
      id: 'lp_ac_auto',
      type: 'Llave de paso',
      spec: `Llave de paso AC ½" — ensamblaje U completo (estrategia ${strategy})`,
      pos: [x_lp_ac, y_alimentacion, Z_LP_FRONT],
      color: 'llave_ac',
      system: 'AC',
      isSwitchboard: true,
    });
    result.pipes_ac.push({
      id: 'lp_ac_down',
      label: 'AC — Bajada ½" del LP al piso',
      start: [x_lp_ac_right, y_alimentacion, Z_LP_FRONT] as Vec3,
      end: [x_lp_ac_right, y_piso, Z_LP_FRONT] as Vec3,
      diam: '1/2"',
      len: y_alimentacion - y_piso,
      system: 'AC',
      mat: 'CPVC',
    });
    result.accessories_ac.push({
      id: 'codo_piso_ac_front',
      type: 'Codo 90°',
      spec: 'Codo CPVC ½" 90° (LP frontal → cruce piso AC)',
      pos: [x_lp_ac_out, y_piso, Z_LP_FRONT],
      color: 'acc_ac',
      system: 'AC',
    });
    result.pipes_ac.push({
      id: 'cruce_piso_ac',
      label: 'AC — Cruce por contrapiso ½" (frontal→trasero)',
      start: [x_lp_ac_out, y_piso, Z_LP_FRONT] as Vec3,
      end: [x_lp_ac_out, y_piso, Z_RISER_AC] as Vec3,
      diam: '1/2"',
      len: Z_LP_FRONT - Z_RISER_AC,
      system: 'AC',
      mat: 'CPVC',
    });
    result.accessories_ac.push({
      id: 'codo_piso_ac_back',
      type: 'Codo 90°',
      spec: 'Codo CPVC ½" 90° (cruce piso → subida muro trasero AC)',
      pos: [x_lp_ac_out, y_piso, Z_RISER_AC],
      color: 'acc_ac',
      system: 'AC',
    });
    result.pipes_ac.push({
      id: 'subida_main_ac',
      label: 'AC — Subida ½" al main',
      start: [x_lp_ac_out, y_piso, Z_RISER_AC] as Vec3,
      end: [x_lp_ac_out, y_alimentacion, Z_RISER_AC] as Vec3,
      diam: '1/2"',
      len: y_alimentacion - y_piso,
      system: 'AC',
      mat: 'CPVC',
    });
    result.accessories_ac.push({
      id: 'codo_main_ac_start',
      type: 'Codo 90°',
      spec: 'Codo CPVC ½" 90° (subida → main AC)',
      pos: [x_lp_ac_out, y_alimentacion, Z_RISER_AC],
      color: 'acc_ac',
      system: 'AC',
    });

    // Main AC: cubre desde el menor de (LP_AC_OUT, primer xAc) hasta el último
    const acRiserXs = sanitaries
      .filter((s) => s.has_ac)
      .map((s) => s.pos[0] + AC_X_OFFSET);
    const x_main_start_ac = Math.min(x_lp_ac_out, Math.min(...acRiserXs));
    const x_max_ac = Math.min(Math.max(...acRiserXs), room.w);
    result.pipes_ac.push({
      id: 'main_ac',
      label: `AC — Distribución principal ½" (${strategy})`,
      start: [x_main_start_ac, y_alimentacion, Z_RISER_AC] as Vec3,
      end: [x_max_ac, y_alimentacion, Z_RISER_AC] as Vec3,
      diam: '1/2"',
      len: x_max_ac - x_main_start_ac,
      system: 'AC',
      mat: 'CPVC',
    });
  }

  // Ramales por sanitario
  sanitaries.forEach((s, idx) => {
    const id = `s${idx}`;
    const outletY = getPointOfUseY(s);

    // ── TINA ROTADA: ramal sube por el EJE del aparato hasta la
    // grifería (v1.22). Antes salía del muro lateral X=room.w, ahora
    // sube en el centro del aparato y termina en 2 llaves separadas con
    // tee mezcladora + caño que vierte agua a la tina. La altura de los
    // grifos coincide con el borde superior de la tina (~0.63 m PAEBA).
    const isTinaRotada =
      s.type === 'Ducha' &&
      s.subtype.toLowerCase().includes('tina') &&
      s.w < s.d; // rotada: lado corto en X
    if (isTinaRotada) {
      // Altura del caño y de los 2 grifos (sobre el borde de la tina).
      const tinaH = 0.52;
      const yGriferia = tinaH + 0.11; // 0.63 m
      const yCanio = tinaH + 0.20; // ~0.72 m donde sale el caño hacia adelante
      // Posición del juego: en el eje X del aparato, sobre la cabecera
      // (extremo Z menor, pegado al muro principal Z=0).
      const xCenter = s.pos[0];
      const zCabBase = Math.max(Z_RISER_AF + 0.04, s.pos[2] - s.d / 2 + 0.13);
      // Separación entre los 2 grifos AF/AC (10 cm horizontal en X)
      const dxGrifo = 0.07;
      const xTee = Math.min(x_max - 0.05, xCenter); // tee en main alineada con eje
      const yMain = y_alimentacion;

      const buildTinaBranch = (sys: 'AF' | 'AC'): void => {
        const isAf = sys === 'AF';
        const matStr = isAf ? 'PVC' : 'CPVC';
        const colorAcc = isAf ? 'acc' : 'acc_ac';
        const zMain = isAf ? Z_RISER_AF : Z_RISER_AC;
        // Cada grifo a la izquierda/derecha del eje del aparato
        const xGrifo = isAf ? xCenter - dxGrifo : xCenter + dxGrifo;
        const suf = isAf ? 'af' : 'ac';
        const pipesList = isAf ? result.pipes : result.pipes_ac;
        const accList = isAf ? result.accessories : result.accessories_ac;

        // 1) Tee en el main (muro trasero) en x = eje del aparato
        accList.push({
          id: `${id}_${suf}_tee`,
          type: 'Tee',
          spec: `Tee ${matStr} 1/2"×1/2"×1/2" (ramal tina)`,
          pos: [xTee, yMain, zMain],
          color: colorAcc,
          system: sys,
        } as never);
        // 2) Bajada vertical al contrapiso
        pipesList.push({
          id: `${id}_${suf}_down`,
          label: `${sys} — Bajada al piso (ramal tina)`,
          start: [xTee, yMain, zMain] as Vec3,
          end: [xTee, yGriferia, zMain] as Vec3,
          diam: '1/2"',
          len: yGriferia - yMain,
          system: sys,
          mat: isAf ? 'PVC-P' : 'CPVC',
        } as never);
        accList.push({
          id: `${id}_${suf}_codo_piso1`,
          type: 'Codo 90°',
          spec: `Codo ${matStr} ½" 90° (bajada → cruce piso tina)`,
          pos: [xTee, yGriferia, zMain],
          color: colorAcc,
          system: sys,
        } as never);
        // 3) Cruce Z por contrapiso desde muro trasero hasta cabecera
        pipesList.push({
          id: `${id}_${suf}_cruceZ`,
          label: `${sys} — Cruce piso Z (ramal tina)`,
          start: [xTee, yGriferia, zMain] as Vec3,
          end: [xTee, yGriferia, zCabBase] as Vec3,
          diam: '1/2"',
          len: zCabBase - zMain,
          system: sys,
          mat: isAf ? 'PVC-P' : 'CPVC',
        } as never);
        accList.push({
          id: `${id}_${suf}_codo_piso2`,
          type: 'Codo 90°',
          spec: `Codo ${matStr} ½" 90° (cruce Z → cruce X tina)`,
          pos: [xTee, yGriferia, zCabBase],
          color: colorAcc,
          system: sys,
        } as never);
        // 4) Cruce X por contrapiso desde eje del main hasta la columna
        //    del grifo (xGrifo)
        pipesList.push({
          id: `${id}_${suf}_cruceX`,
          label: `${sys} — Cruce piso X (ramal tina)`,
          start: [xTee, yGriferia, zCabBase] as Vec3,
          end: [xGrifo, yGriferia, zCabBase] as Vec3,
          diam: '1/2"',
          len: Math.abs(xGrifo - xTee),
          system: sys,
          mat: isAf ? 'PVC-P' : 'CPVC',
        } as never);
        accList.push({
          id: `${id}_${suf}_codo_piso3`,
          type: 'Codo 90°',
          spec: `Codo ${matStr} ½" 90° (cruce X → subida grifo tina)`,
          pos: [xGrifo, yGriferia, zCabBase],
          color: colorAcc,
          system: sys,
        } as never);
        // 5) Subida vertical desde el piso a la altura de la grifería
        pipesList.push({
          id: `${id}_${suf}_riser`,
          label: `${sys} — Montante tina ½" (eje del aparato)`,
          start: [xGrifo, yGriferia, zCabBase] as Vec3,
          end: [xGrifo, yGriferia, zCabBase] as Vec3,
          diam: '1/2"',
          len: 0,
          system: sys,
          mat: isAf ? 'PVC-P' : 'CPVC',
        } as never);
        // 6) Llave (válvula) en la cabeza del riser — el grifo manual de
        //    apertura/cierre que el usuario gira para regular el agua
        accList.push({
          id: `${id}_${suf}_grifo`,
          type: 'Llave de tina',
          spec: `Llave ${sys} ½" (juego de tina — muro corto principal)`,
          pos: [xGrifo, yGriferia, zCabBase],
          color: colorAcc,
          system: sys,
        } as never);
      };

      buildTinaBranch('AF');
      if (s.has_ac) buildTinaBranch('AC');

      // 7) Tubo horizontal que UNE las 2 llaves (AF + AC) pasando por la
      //    tee mezcladora en el centro. Material CPVC porque sale agua
      //    mezclada/caliente al caño.
      result.pipes_ac.push({
        id: `${id}_tina_bridge`,
        label: `AC — Tubo horizontal entre llaves de tina ½"`,
        start: [xCenter - dxGrifo, yGriferia, zCabBase] as Vec3,
        end: [xCenter + dxGrifo, yGriferia, zCabBase] as Vec3,
        diam: '1/2"',
        len: dxGrifo * 2,
        system: 'AC',
        mat: 'CPVC',
      });
      result.accessories_ac.push({
        id: `${id}_tina_mixer`,
        type: 'Tee mezcladora',
        spec: 'Tee mezcladora ½" (tina — AF + AC → caño)',
        pos: [xCenter, yGriferia, zCabBase],
        color: 'acc_ac',
        system: 'AC',
      });
      // 8) Caño vertical desde la tee mezcladora — vierte agua a la tina
      result.pipes_ac.push({
        id: `${id}_tina_canio`,
        label: `AC — Caño de tina ½"`,
        start: [xCenter, yGriferia, zCabBase] as Vec3,
        end: [xCenter, yCanio, zCabBase + 0.04] as Vec3,
        diam: '1/2"',
        len: 0.12,
        system: 'AC',
        mat: 'CPVC',
      });
      result.accessories_ac.push({
        id: `${id}_tina_pico`,
        type: 'Caño de tina',
        spec: 'Caño de salida ½" (juego de tina)',
        pos: [xCenter, yCanio, zCabBase + 0.06],
        color: 'acc_ac',
        system: 'AC',
      });

      return; // saltamos el ramal estándar para esta tina
    }

    // ── Ramal AF (resto de aparatos: muro trasero) ──────────────
    const xAf = s.pos[0] + afXOffset(s);
    const isAfTerminal = Math.abs(xAf - x_max) < 0.001;
    const zAfRiser = fixtureRiserZ(s, Z_RISER_AF);
    const afNeedsDogleg = isLateralLavadero(s) && Math.abs(zAfRiser - cfg.z_main_af) > 0.001;

    // Tee ½"×½"×½" en el main. NO es reductora: la única reducción del
    // sistema ya se hizo antes del LP (v1.17). Todos los tubos y
    // accesorios después del LP son ½".
    result.accessories.push({
      id: `${id}_af_tee`,
      type: isAfTerminal ? 'Codo 90°' : 'Tee',
      spec: 'Tee PVC-P 1/2"×1/2"×1/2"',
      pos: [xAf, cfg.y_main_af, cfg.z_main_af],
      color: 'acc',
      system: 'AF',
    });
    if (afNeedsDogleg) {
      result.pipes.push({
        id: `${id}_af_lateral_branch`,
        label: `AF — Quiebre hacia ${s.type} ½"`,
        start: [xAf, cfg.y_main_af, cfg.z_main_af] as Vec3,
        end: [xAf, cfg.y_main_af, zAfRiser] as Vec3,
        diam: '1/2"',
        len: Math.abs(cfg.z_main_af - zAfRiser),
        system: 'AF',
        mat: 'PVC-P',
      });
      result.accessories.push({
        id: `${id}_af_codo_lateral`,
        type: 'Codo 90°',
        spec: `Codo PVC ½" 90° (quiebre lateral ${s.type} AF)`,
        pos: [xAf, cfg.y_main_af, zAfRiser],
        color: 'acc',
        system: 'AF',
      });
    } else if (cfg.needs_bridge_af) {
      // Bridge horizontal main→muro (solo si el main no corre en el muro)
      result.pipes.push({
        id: `${id}_af_branch`,
        label: `AF — Tramo piso→muro ${s.type} ½"`,
        start: [xAf, cfg.y_main_af, cfg.z_main_af] as Vec3,
        end: [xAf, cfg.y_main_af, Z_RISER_AF] as Vec3,
        diam: '1/2"',
        len: Math.abs(cfg.z_main_af - Z_RISER_AF),
        system: 'AF',
        mat: 'PVC-P',
      });
      result.accessories.push({
        id: `${id}_af_codo_piso`,
        type: 'Codo 90°',
        spec: `Codo PVC ½" 90° (piso → muro ${s.type} AF)`,
        pos: [xAf, cfg.y_main_af, Z_RISER_AF],
        color: 'acc',
        system: 'AF',
      });
    }
    // Montante AF (sube por el muro hasta el punto de uso)
    result.pipes.push({
      id: `${id}_af_riser`,
      label: `AF — Montante ${s.type} ½"`,
      start: [xAf, cfg.y_main_af, zAfRiser] as Vec3,
      end: [xAf, outletY, zAfRiser] as Vec3,
      diam: '1/2"',
      len: Math.abs(outletY - cfg.y_main_af),
      system: 'AF',
      mat: 'PVC-P',
    });
    // Codo galvanizado de salida en muro
    result.accessories.push({
      id: `${id}_af_out`,
      type: 'Codo galvanizado',
      spec: `Codo galvanizado ½" (punto salida ${s.type} AF)`,
      pos: [xAf, outletY, zAfRiser],
      color: 'acc',
      system: 'AF',
    });

    // ── Ramal AC (solo si has_ac) ────────────────────────────────
    if (!s.has_ac) return;
    const xAc = s.pos[0] + AC_X_OFFSET;
    const zAcRiser = fixtureRiserZ(s, Z_RISER_AC);
    const acNeedsDogleg = isLateralLavadero(s) && Math.abs(zAcRiser - cfg.z_main_ac) > 0.001;
    const acTerminalX = Math.max(
      ...sanitaries.filter((ss) => ss.has_ac).map((ss) => ss.pos[0] + AC_X_OFFSET),
    );
    const acStartTerminalX = Math.min(
      ...sanitaries.filter((ss) => ss.has_ac).map((ss) => ss.pos[0] + AC_X_OFFSET),
    );
    const isAcTerminal =
      Math.abs(xAc - acStartTerminalX) < 0.001 || Math.abs(xAc - acTerminalX) < 0.001;

    if (acNeedsDogleg) {
      result.accessories_ac.push({
        id: `${id}_ac_codo_lateral`,
        type: 'Codo 90°',
        spec: `Codo CPVC ½" 90° (quiebre lateral ${s.type} AC)`,
        pos: [xAc, cfg.y_main_ac, zAcRiser],
        color: 'acc_ac',
        system: 'AC',
      });
      result.pipes_ac.push({
        id: `${id}_ac_lateral_branch`,
        label: `AC — Quiebre hacia ${s.type} ½"`,
        start: [xAc, cfg.y_main_ac, cfg.z_main_ac] as Vec3,
        end: [xAc, cfg.y_main_ac, zAcRiser] as Vec3,
        diam: '1/2"',
        len: Math.abs(cfg.z_main_ac - zAcRiser),
        system: 'AC',
        mat: 'CPVC',
      });
    } else if (cfg.needs_bridge_ac) {
      // AC viene del piso: tee en main → bridge al muro → codo → montante.
      result.accessories_ac.push({
        id: `${id}_ac_codo_piso`,
        type: 'Codo 90°',
        spec: `Codo CPVC ½" 90° (piso → muro ${s.type} AC)`,
        pos: [xAc, cfg.y_main_ac, Z_RISER_AC],
        color: 'acc_ac',
        system: 'AC',
      });
      result.pipes_ac.push({
        id: `${id}_ac_branch`,
        label: `AC — Tramo piso→muro ${s.type} ½"`,
        start: [xAc, cfg.y_main_ac, cfg.z_main_ac] as Vec3,
        end: [xAc, cfg.y_main_ac, Z_RISER_AC] as Vec3,
        diam: '1/2"',
        len: Math.abs(cfg.z_main_ac - Z_RISER_AC),
        system: 'AC',
        mat: 'CPVC',
      });
    } else {
      // muro_dual: AC ya está en el muro, tee directo.
      // Tee ½"×½"×½" — no reductora (la única reducción del sistema AC
      // está antes del LP, v1.17).
      result.accessories_ac.push({
        id: `${id}_ac_tee`,
        type: isAcTerminal ? 'Codo 90°' : 'Tee',
        spec: isAcTerminal ? 'Codo CPVC ½" 90° (terminal main AC)' : 'Tee CPVC 1/2"×1/2"×1/2"',
        pos: [xAc, cfg.y_main_ac, cfg.z_main_ac],
        color: 'acc_ac',
        system: 'AC',
      });
    }
    // Montante AC (sube por el muro hasta el punto de uso)
    result.pipes_ac.push({
      id: `${id}_ac_riser`,
      label: `AC — Montante ${s.type} ½"`,
      start: [xAc, cfg.y_main_ac, zAcRiser] as Vec3,
      end: [xAc, outletY, zAcRiser] as Vec3,
      diam: '1/2"',
      len: Math.abs(outletY - cfg.y_main_ac),
      system: 'AC',
      mat: 'CPVC',
    });
    // Punto de uso AC
    result.accessories_ac.push({
      id: `${id}_ac_out`,
      type: 'Adaptador + niple galvanizado',
      spec: `Adaptador CPVC-rosca ½" + niple galv. (salida AC ${s.type})`,
      pos: [xAc, outletY, zAcRiser],
      color: 'acc_ac',
      system: 'AC',
    });

    // ── DUCHA: tramo a la regadera (no tina) ─────────────────────
    // En una ducha real, los risers AF y AC suben hasta la altura de la
    // mezcladora (~1.05 m PAEBA). Ahí se mezclan en una T y un solo tubo
    // único (agua tibia/caliente) sube hasta el brazo de la regadera
    // (~2.05 m). El usuario lo pidió explícitamente: "tubo que sale de
    // enmedio de estos dos unidos por una t". Solo aplica a ducha
    // estándar; en tina la grifería va horizontal en el muro corto.
    const isDuchaEstandar =
      s.type === 'Ducha' &&
      !s.subtype.toLowerCase().includes('tina') &&
      !s.subtype.toLowerCase().includes('bañera');
    if (isDuchaEstandar) {
      const xMixer = (xAf + xAc) / 2;
      const zMixer = (Z_RISER_AF + Z_RISER_AC) / 2;
      const yMixer = outletY; // altura de la mezcladora (1.05 m)
      // Altura del cabezal: 1 m sobre la T mezcladora (v1.22 — feedback
      // del usuario: "1m de tubo de alto desde la T mezcladora").
      const yArm = yMixer + 1.0;
      // Tee mezcladora (recibe AF + AC, sale un solo tubo arriba)
      result.accessories_ac.push({
        id: `${id}_ducha_mixer`,
        type: 'Tee mezcladora',
        spec: 'Tee mezcladora ½" (ducha — AF + AC → regadera)',
        pos: [xMixer, yMixer, zMixer],
        color: 'acc_ac',
        system: 'AC',
      });
      // Subida vertical a la regadera (tubo único de salida — agua mezclada)
      result.pipes_ac.push({
        id: `${id}_ducha_head`,
        label: `AC — Subida a regadera ½" (${s.type})`,
        start: [xMixer, yMixer, zMixer] as Vec3,
        end: [xMixer, yArm, zMixer] as Vec3,
        diam: '1/2"',
        len: yArm - yMixer,
        system: 'AC',
        mat: 'CPVC',
      });
      // Brazo horizontal hacia el CENTRO del aparato de ducha (v1.22).
      // Antes el brazo era 18 cm fijos; ahora la longitud llega al centro
      // del plato de la ducha (s.pos[2]) para que el cabezal quede sobre
      // la zona de uso, no cerca del muro.
      const armLen = Math.max(0.15, s.pos[2] - zMixer);
      result.pipes_ac.push({
        id: `${id}_ducha_brazo`,
        label: `AC — Brazo regadera ½" (${s.type})`,
        start: [xMixer, yArm, zMixer] as Vec3,
        end: [xMixer, yArm, zMixer + armLen] as Vec3,
        diam: '1/2"',
        len: armLen,
        system: 'AC',
        mat: 'CPVC',
      });
      result.accessories_ac.push({
        id: `${id}_ducha_regadera`,
        type: 'Regadera',
        spec: 'Cabezal de regadera ½" (ducha)',
        pos: [xMixer, yArm, zMixer + armLen],
        color: 'acc_ac',
        system: 'AC',
      });
    }
  });

  return result;
}

/**
 * Devuelve la altura Y del punto de uso de un sanitario para alimentación AF/AC.
 * Mapea cada tipo de aparato a la altura PAEBA correspondiente.
 */
function getPointOfUseY(s: Sanitary): number {
  switch (s.type) {
    case 'Inodoro':
      return NPT_HEIGHTS.inodoro_af;
    case 'Lavatorio':
      return NPT_HEIGHTS.lavatorio_af;
    case 'Ducha':
      return NPT_HEIGHTS.ducha_llave;
    case 'Urinario':
      return NPT_HEIGHTS.urinario_af;
    case 'Lavadero de ropa':
      return NPT_HEIGHTS.lav_ropa_af;
    case 'Lavadero de platos':
      return NPT_HEIGHTS.lav_platos_af;
  }
}
